import type {EntityType} from 'src/types/ats-entities';
import type {TaskCandidateDetailsRef} from 'src/types/task-and-reminder';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import * as React from 'react';
import debounce from 'lodash/debounce';
import {useDispatch, useSelector} from 'react-redux';

import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';

import {FALLBACK_CANDIDATE_ENTITY_TYPE} from 'src/constants/job-match-addon';
import {useCrmRedesignedEnabled} from 'src/hooks/product-flags';
import {selectCandidateEntityType} from 'src/selectors/ats-entities';
import * as reduxApi from 'src/utils/redux-api-v2';

import {mergeTypeaheadSelectedOption} from 'src/components/task-and-reminder/lib/merge-typeahead-selected-option';
import {INITIAL_FILTER_STATE} from 'src/components/crm/candidates/constant.ts';


const DEFAULT_CANDIDATE_OPTIONS_LIMIT = 10;

type CandidateEntityRow = {
  id: string;
  full_name?: string;
  primary_email?: string;
};

type TypeaheadEntitiesResponse = {
  entities?: Array<CandidateEntityRow>;
};

type CandidateOption = {
  id: string;
  name: string;
};

type TaskCandidateSelectFieldProps = {
  label: string;
  placeholder: string;
  value: TaskCandidateDetailsRef | null;
  onChange: (next: TaskCandidateDetailsRef | null) => void;
  className?: string;
};

const candidateDisplayName = (entity: CandidateEntityRow): string => {
  const fullName =
    typeof entity.full_name === 'string' ? entity.full_name.trim() : '';
  if (fullName) {
    return fullName;
  }
  const email =
    typeof entity.primary_email === 'string' ? entity.primary_email.trim() : '';
  return email;
};

export const TaskCandidateSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  className,
}: TaskCandidateSelectFieldProps) => {
  const dispatch = useDispatch();
  const isCrmEnabled = useCrmRedesignedEnabled();
  const candidateEntityTypeFromStore = useSelector(selectCandidateEntityType);
  const entityType: EntityType =
    candidateEntityTypeFromStore ?? FALLBACK_CANDIDATE_ENTITY_TYPE;

  const [entities, setEntities] = React.useState<Array<CandidateOption>>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const typeaheadOptions = React.useMemo(
    () =>
      mergeTypeaheadSelectedOption(
        entities.map((entity) => ({
          key: entity.id,
          label: entity.name,
        })),
        value?.id ? {id: String(value.id), label: value.name ?? ''} : null,
      ),
    [entities, value?.id, value?.name],
  );

  const selectedKeys = React.useMemo(
    () => (value?.id ? [String(value.id)] : []),
    [value?.id],
  );

  const fetchCandidates = React.useCallback(
    async (query: string): Promise<Array<CandidateOption>> => {
      if (isCrmEnabled) {
        const response = await dispatch(
          reduxApi.post(
            '/api/candidates/search',
            {...INITIAL_FILTER_STATE, q: query ? [query] : []},
            {
              searchId: '-1',
              type: 'applied',
              page: '0',
              pageSize: String(DEFAULT_CANDIDATE_OPTIONS_LIMIT),
              sortBy: 'OVERALL_SCORE',
            },
            {apiPath: '/trmapi'},
          ),
        );
        const list = response?.content;
        const rows = Array.isArray(list) ? list : [];
        return rows.reduce<Array<CandidateOption>>((acc, item) => {
          const candidate = item?.candidate;
          if (candidate?.id != null) {
            acc.push({
              id: String(candidate.id),
              name: candidate.name ?? candidate?.resume?.email ?? '',
            });
          }
          return acc;
        }, []);
      }

      const response = (await dispatch(
        reduxApi.get(`typeahead/entities/${entityType}`, {
          search_string: query,
          limit: DEFAULT_CANDIDATE_OPTIONS_LIMIT,
          offset: 0,
        }),
      )) as TypeaheadEntitiesResponse | null | undefined;
      const list = response?.entities;
      const rows = Array.isArray(list) ? list : [];
      return rows.map((entity) => ({
        id: String(entity.id),
        name: candidateDisplayName(entity),
      }));
    },
    [dispatch, entityType, isCrmEnabled],
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query: string) => {
        void (async () => {
          setIsLoading(true);
          try {
            const list = await fetchCandidates(query);
            setEntities(list);
          } finally {
            setIsLoading(false);
          }
        })();
      }, 400),
    [fetchCandidates],
  );

  React.useEffect(() => {
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchCandidates('');
        setEntities(list);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchCandidates]);

  const handleSelect = (option: MenuOption) => {
    const row = entities.find((entity) => entity.id === option.key);
    const name = row ? row.name : option.label || '';
    onChange({id: option.key, name});
  };

  const handleClear = () => {
    onChange(null);
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchCandidates('');
        setEntities(list);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className={className}>
      <SimpleTypeahead
        label={label}
        placeholder={placeholder}
        selectedKeys={selectedKeys}
        menuOpenOffset={0}
        onSearch={(evt) => {
          debouncedSearch(evt.target.value);
        }}
        onSelect={handleSelect}
        onClear={handleClear}
        options={typeaheadOptions}
        isLoading={isLoading}
        testId="task-candidate-select"
      />
    </div>
  );
};
